import { Component, signal, ChangeDetectionStrategy, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidebarItem } from '../sidebar-item/sidebar-item';
import { LayoutService } from '../../../core/services/layout.service';
import { getAllowedRoutesForRole } from '../../../core/config/menu-config';
import {
  ArrowTurnUpIcon,
  Chart03Icon,
  DashboardSquare03Icon,
  Grid02Icon,
  MentoringIcon,
  MenuCollapseIcon,
  MessageUpload01Icon,
  NewReleasesIcon,
  Notification01Icon,
  Search01Icon,
  Settings01Icon,
  Task01Icon,
  Upload01Icon,
  User02Icon,
  UserSwitchIcon,
  FolderManagementIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIconComponent } from "@hugeicons/angular";

interface MenuItem {
  label: string;
  route: string;
  icon?: string | null;
  hugeIcon?: any | null;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, SidebarItem, HugeiconsIconComponent],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidebar {
  collapsed = signal(false);

  // Expose all Hugeicons icons as class properties
  DashboardIcon = DashboardSquare03Icon;
  RoleRegionIcon = MentoringIcon;
  ReportsIcon = Chart03Icon;
  ArrowTurnUpIcon = ArrowTurnUpIcon;
  Task01Icon = Task01Icon;
  LookUpsIcon = Search01Icon;
  ApprovalMatrixIcon = Grid02Icon;
  NotificationMatrixIcon = Notification01Icon;
  ApprovalsIcon = NewReleasesIcon;
  MassUploadIcon = MessageUpload01Icon;
  MenuCollapseIcon = MenuCollapseIcon;
  UserSwitchIcon = UserSwitchIcon;
  AdminClaimsIcon = FolderManagementIcon;
  constructor(private layoutService: LayoutService) {
    // Sync mobile menu state from service
    effect(() => {
      const isOpen = this.layoutService.mobileMenuOpen();
      if (!isOpen && this.collapsed()) {
        // Auto-close mobile menu when sidebar is collapsed on desktop
      }
    });
  }

  get mobileOpen() {
    return this.layoutService.mobileMenuOpen;
  }

  // All available menu items
  private allMenuItems: MenuItem[] = [
    { label: 'Dashboard', route: '/dashboard', hugeIcon: this.DashboardIcon },
    { label: 'DM Sales Dashboard', route: '/dm-sale-dashboard', hugeIcon: this.DashboardIcon },
    { label: 'Users Management', route: '/role-region', hugeIcon: this.RoleRegionIcon },
    // { label: 'Reports', route: '/reports', hugeIcon: this.ReportsIcon },
    { label: 'LookUps', route: '/lookups', hugeIcon: this.LookUpsIcon },
    { label: 'Approval Matrix', route: '/approval-matrix', hugeIcon: this.ApprovalMatrixIcon },
    { label: 'Notification Matrix', route: '/notification-matrix', hugeIcon: this.NotificationMatrixIcon },
    // { label: 'Approvals', route: '/approvals', hugeIcon: this.ApprovalsIcon },
    { label: 'Mass Upload', route: '/mass-upload', hugeIcon: this.MassUploadIcon },
    { label: 'Task List', route: '/task-list', hugeIcon: this.Task01Icon },
    { label: 'Claim List', route: '/claim-list', hugeIcon: this.ArrowTurnUpIcon },
    { label: 'Approval Reassignment', route: '/approval-reassign', hugeIcon: this.UserSwitchIcon },
    { label: 'Claims Management', route: '/admin-claims', hugeIcon: this.AdminClaimsIcon }
  ];

  // Computed menu items based on selectedRole
  menuItems = computed(() => {
    const selectedRole = this.layoutService.selectedRole();
    const allowedRoutes = getAllowedRoutesForRole(selectedRole);

    // Filter menu items based on allowed routes for the selected role
    return this.allMenuItems.filter(item => allowedRoutes.includes(item.route));
  });

  /** On mobile/tablet always false (expanded); on desktop uses collapsed state */
  effectiveCollapsed = computed(() =>
    this.layoutService.isDesktop() ? this.collapsed() : false
  );

  toggleCollapse(): void {
    if (this.layoutService.isDesktop()) {
      this.collapsed.update(val => !val);
    } else {
      // On mobile/tablet: close the sidebar drawer (same as hamburger menu)
      this.layoutService.closeMobileMenu();
    }
  }

  closeMobile(): void {
    this.layoutService.closeMobileMenu();
  }
}
